import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { watchCartCount } from '@theme/scdm-cart-count';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  connectedCallback() {
    super.connectedCallback();

    this.unsubscribeCount = watchCartCount((state) => this.renderCartBubble(state));
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.unsubscribeCount?.();
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Renders the cart bubble.
   * @param {{count: number|null, empty: boolean}} state - Presentation only.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (state, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', state.empty);
    this.refs.cartBubble.classList.toggle('visually-hidden', state.empty);
    this.classList.toggle('header-actions__cart-icon--has-cart', !state.empty);
    if (!animate || state.empty || state.count === null) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const counter = this.refs.cartBubbleCount.querySelector('scdm-commercial-count');
    this.renderCartBubble(counter?.countState || { count: null, empty: this.refs.cartBubble.classList.contains('visually-hidden') }, false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
